import _ from 'lodash';
import BenefitsApi from '@/api/BenefitsApi';
import PromotionsApi from '@/api/PromotionsApi';
import Segment from '@/Segment';
import promotions from '@/constants/promotions';

const state = {
  benefitId: null,
  benefitType: null,
  benefitAmount: null,
  benefitPromotion: null,
  benefitPercentage: null,
  registeredUsingHeverPromotion: false,
  redeemed: false,
  isAppleBenefit: false,
};

const getters = {
  BENEFIT_TYPES: state => promotions.BENEFIT_TYPES,
  BENEFIT_CREATION_STATUS: state => promotions.BENEFIT_CREATION_STATUS,
  discountedPrice: (state, getters, rootState) => {
    const monthlyPrice = rootState.subscription?.pricing?.monthly;
    if (!monthlyPrice || !state.benefitPercentage) {
      return monthlyPrice;
    }
    return Math.round((monthlyPrice - (monthlyPrice * (state.benefitPercentage / 100))) * 100) / 100;
  },
  isPercentageDiscountBenefit: state => state.benefitType === promotions.BENEFIT_TYPES.PERCENTAGE_DISCOUNT,
  monthsText: state => {
    switch (state.benefitAmount) {
    case 1:
      return 'חודש';
    case 2:
      return 'חודשיים';
    default:
      return `${state.benefitAmount} חודשים`;
    }
  },
  promotion: state => state.benefitPromotion,
};

function isBenefitAndSubscriptionProviderMatch(benefit, rootGetters) {
  const isAppleBenefit = benefit.isAppleBenefit === true;
  const isAppleProvider = rootGetters['subscription/isApplePaymentProvider'];
  return isAppleProvider === isAppleBenefit;
}

const actions = {
  async initBenefits({ commit, rootGetters, dispatch }) {
    const [
      benefit,
      registeredUsingHeverPromotion,
    ] = await Promise.all([
      BenefitsApi.getActiveBenefit(),
      PromotionsApi.isRegisteredUsingHeverPromotion(),
    ]);
    if (!_.isEmpty(benefit)) {
      // Make sure we got update subscriptions
      await dispatch('subscription/fetchSubscription', null, { root: true });
      if (isBenefitAndSubscriptionProviderMatch(benefit, rootGetters)) {
        commit('setBenefitId', benefit._id);
        commit('setBenefitType', benefit.benefitType);
        commit('setBenefitAmount', benefit.benefitAmount);
        commit('setBenefitPercentage', benefit.benefitPercentage);
        commit('setBenefitPromotion', benefit.promotion);
        commit('setBenefitRedeemed', benefit.redeemed);
        commit('setIsAppleBenefit', benefit.isAppleBenefit);
      }
    }
    if (registeredUsingHeverPromotion) {
      commit('setIsRegisteredUsingHeverPromotion', registeredUsingHeverPromotion);
    }
  },
  async redeemBenefit({ state }) {
    await BenefitsApi.redeemBenefit(state.benefitId);
    Segment.trackUserGot('BenefitRedeemed', { benefitType: state.benefitType, benefitAmount: state.benefitAmount });
  },
  async createBenefit({ dispatch }, promoCode) {
    const result = await BenefitsApi.createBenefitByPromoCode(promoCode);
    await dispatch('initBenefits');
    return result;
  },
  async createBenefitByPromoSource({ dispatch }, { promoSource }) {
    const promotion = await PromotionsApi.getPromotionForSource(promoSource);
    return await dispatch('createBenefit', promotion.promoCode);
  },
  async hasHeverBenefit() {
    const { hasHeverBenefit } = await BenefitsApi.hasHeverBenefit();
    return hasHeverBenefit;
  },
};

const mutations = {
  setBenefitId(state, benefitId) {
    state.benefitId = benefitId;
  },
  setBenefitType(state, benefitType) {
    state.benefitType = benefitType;
  },
  setBenefitAmount(state, benefitAmount) {
    state.benefitAmount = benefitAmount;
  },
  setBenefitPromotion(state, benefitPromotion) {
    state.benefitPromotion = benefitPromotion;
  },
  setBenefitPercentage(state, benefitPercentage) {
    state.benefitPercentage = benefitPercentage;
  },
  setIsRegisteredUsingHeverPromotion(state, registeredUsingHeverPromotion) {
    state.registeredUsingHeverPromotion = registeredUsingHeverPromotion;
  },
  setBenefitRedeemed(state, redeemed) {
    state.redeemed = redeemed;
  },
  setIsAppleBenefit(state, isAppleBenefit) {
    state.isAppleBenefit = isAppleBenefit === true;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
